"use client";

import { useContext, useEffect, useState } from "react";

import { createPath } from "@/service/routing/routing";
import { SuperLink as Link } from "@/components/common/super-link";
import CookieIcon from "@/svg/cookie/cookie-icon";

import { UserContext } from "@/contexts/user-context";
import { setGoogleConsentMode } from "@/service/logs/log";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export const CookieSettings = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [switchOnAnalytics, setSwitchOnAnalytics] = useState(false);
  const [switchOnAds, setSwitchOnAds] = useState(false);
  const { isLoggedIn } = useContext(UserContext);

  let startedAnalytics = false;
  let startedAdvertising = false;

  function openSettings() {
    setModalOpen(true);
  }

  function closeSettings() {
    setModalOpen(false);
  }

  function closePanel() {
    setPanelOpen(false);
  }

  function openPanel() {
    setPanelOpen(true);
  }

  function accept() {
    localStorage.setItem("cookie-accept", "1");

    if (
      startedAnalytics === false &&
      localStorage.getItem("analytics") === "1"
    ) {
      startedAnalytics = true;
    }

    if (
      startedAdvertising === false &&
      localStorage.getItem("advertising") === "1"
    ) {
      startedAdvertising = true;
    }
  }

  useEffect(() => {
    if (isLoggedIn) {
      return;
    }

    if (document.querySelector("#privacy-policy-open-cookies")) {
      document
        .querySelector("#privacy-policy-open-cookies")
        ?.addEventListener("click", (e) => {
          e.preventDefault();
          openSettings();
        });
    }

    let isAnalytics = localStorage.getItem("analytics");
    if (isAnalytics === null) {
      localStorage.setItem("analytics", "0");
      isAnalytics = "0";
    }
    let isAdvertising = localStorage.getItem("advertising");
    if (isAdvertising === null) {
      localStorage.setItem("advertising", "0");
      isAdvertising = "0";
    }
    const isAccept = localStorage.getItem("cookie-accept");

    if (isAnalytics === "1") {
      setSwitchOnAnalytics(true);
      if (isAccept === "1") {
        startedAnalytics = true;
      }
    } else {
      setSwitchOnAnalytics(false);
    }

    if (isAdvertising === "1") {
      setSwitchOnAds(true);
      if (isAccept === "1") {
        startedAdvertising = true;
      }
    } else {
      setSwitchOnAds(false);
    }

    if (isAccept === "0" || isAccept === null) {
      openPanel();
    }
  }, []);

  if (isLoggedIn) {
    return <></>;
  }

  function handleSettings() {
    openSettings();
    closePanel();
  }

  function handleAcceptChoosen() {
    closeSettings();
    closePanel();
    accept();
  }

  function handleBackCookies() {
    closeSettings();
    openPanel();
  }

  function handleDeclineCookie() {
    closeSettings();
    closePanel();
    setSwitchOnAnalytics(false);
    setSwitchOnAds(false);
    localStorage.setItem("analytics", "0");
    localStorage.setItem("advertising", "0");
    localStorage.setItem("cookie-accept", "-1");

    setGoogleConsentMode(isLoggedIn);
  }

  function handleAcceptAll() {
    closePanel();
    setSwitchOnAnalytics(true);
    setSwitchOnAds(true);
    localStorage.setItem("analytics", "1");
    localStorage.setItem("advertising", "1");
    accept();
    setGoogleConsentMode(isLoggedIn);
  }

  function handleSwitchOnAnalytics() {
    setSwitchOnAnalytics(true);
    localStorage.setItem("analytics", "1");
    setGoogleConsentMode(isLoggedIn);
  }

  function handleSwitchOffAnalytics() {
    setSwitchOnAnalytics(false);
    localStorage.setItem("analytics", "0");
    setGoogleConsentMode(isLoggedIn);
  }

  function handleSwitchOnAds() {
    setSwitchOnAds(true);
    localStorage.setItem("advertising", "1");
    setGoogleConsentMode(isLoggedIn);
  }

  function handleSwitchOffAds() {
    setSwitchOnAds(false);
    localStorage.setItem("advertising", "0");
    setGoogleConsentMode(isLoggedIn);
  }

  const modalDescStyle = "text-sm mb-10 text-muted-foreground";
  const modalHeadingStyle = "text-base mb-2 font-bold";
  const modalRadioStyle = `text-xs ml-2`;
  const modalRadioOptionStyle = (isActive) =>
    cn(
      "text-muted-foreground hover:cursor-pointer p-2 transition-all hover:text-remedium-blue-light",
      isActive && "text-remedium-blue",
    );
  return (
    <>
      {modalOpen && (
        <div
          className={cn(
            "font-inter fixed top-0 left-0 z-[199999999] h-screen w-screen overflow-y-scroll rounded-md bg-blue-50 p-5 font-normal shadow-[0_5px_20px_0_rgba(0,0,0,0.13)] md:top-1/2 md:left-1/2 md:h-auto md:w-[600px] md:-translate-x-1/2 md:-translate-y-1/2 md:overflow-y-hidden md:p-10",
            !modalOpen && "hidden",
          )}
        >
          <div>
            <p className={modalHeadingStyle}>Wymagane cookies</p>
            <p className={modalDescStyle}>
              Niezbędne pliki cookie umożliwiają korzystanie z podstawowych
              funkcji, takich jak bezpieczeństwo, zarządzanie siecią i
              dostępność. Można je wyłączyć, zmieniając ustawienia przeglądarki,
              ale może to wpłynąć na funkcjonowanie strony internetowej.
            </p>
          </div>
          <div>
            <p className={modalHeadingStyle}>
              Analityka
              <span className={modalRadioStyle}>
                <span
                  className={modalRadioOptionStyle(switchOnAnalytics)}
                  onClick={handleSwitchOnAnalytics}
                >
                  WŁĄCZ
                </span>
                <span
                  className={modalRadioOptionStyle(!switchOnAnalytics)}
                  onClick={handleSwitchOffAnalytics}
                >
                  WYŁĄCZ
                </span>
              </span>
            </p>
            <p className={modalDescStyle}>
              Chcielibyśmy zbierać dane za pomocą Google Analytics, Google Tag
              Managerci PostHog, aby pomóc nam poprawić naszą stronę internetową
              poprzez zbieranie i raportowanie informacji o tym, jak z niej
              korzystasz. Zbieramy informacje w sposób, który nie identyfikuje
              nikogo bezpośrednio. Więcej informacji na temat tego, jak to
              działa, można znaleźć na naszej stronie dotyczącej{" "}
              <a href={createPath("cookies_policy")} target="_blank">
                polityki cookie
              </a>
              .
            </p>
          </div>

          <div>
            <p className={modalHeadingStyle}>
              Reklamy
              <span className={modalRadioStyle}>
                <span
                  className={modalRadioOptionStyle(switchOnAds)}
                  onClick={handleSwitchOnAds}
                >
                  WŁĄCZ
                </span>
                <span
                  className={modalRadioOptionStyle(!switchOnAds)}
                  onClick={handleSwitchOffAds}
                >
                  WYŁĄCZ
                </span>
              </span>
            </p>
            <p className={modalDescStyle}>
              Chcielibyśmy docierać do Ciebie za pomocą Facebook Pixel, aby
              zapewnić Ci najnowsze informacje o usługach Remedium przez
              platformę Facebook. Więcej informacji o tym, jak to działa,
              znajdziesz na naszej stronie poświęconej{" "}
              <a href={createPath("cookies_policy")} target="_blank">
                polityce cookies
              </a>
              .
            </p>
          </div>

          <p className={modalDescStyle}>
            Aby zobaczyć pełen wykaz ciasteczek{" "}
            <Link href={`${createPath("cookies_policy")}#wykaz-ciasteczek`}>
              klinij tutaj
            </Link>
          </p>

          <div className={"mt-4 flex items-center justify-between gap-4"}>
            <Button variant={"blue"} onClick={handleAcceptChoosen}>
              Akceptuj
            </Button>
            &nbsp;&nbsp;&nbsp;&nbsp;
            <Button variant={"outline"} onClick={handleBackCookies}>
              Wróć
            </Button>
          </div>
        </div>
      )}

      <div
        className={cn(
          `font-inter fixed bottom-[100px] left-1/2 z-[199999999] w-[90vw] -translate-x-1/2 rounded-lg border bg-blue-50 p-3 py-[34px] text-center text-xs leading-[1.58] font-normal shadow-[0_2px_30px_0_rgba(0,0,0,0.09)] sm:w-[570px] md:px-[45px]`,
          !panelOpen && "hidden",
        )}
      >
        <div className={"mb-4 flex items-center justify-center"}>
          <CookieIcon />
        </div>
        <div className={`mb-8 text-sm`}>
          Korzystamy z <strong>cookies</strong>, żeby dostarczyć Ci najlepsze
          treści.
        </div>
        <div
          className={"flex flex-col gap-2 md:flex-row md:items-center md:gap-4"}
        >
          <Button variant={"blue"} onClick={handleAcceptAll}>
            Akceptuję wszystkie
          </Button>
          <Button variant={"outline"} onClick={handleDeclineCookie}>
            Akceptuję niezbędne
          </Button>
          <Button variant={"ghost"} onClick={handleSettings}>
            Ustawienia
          </Button>
        </div>
      </div>
    </>
  );
};
